import { Button, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getTenantByHost } from '@pelicargo/tenants';
import { OnboardingStep } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { COUNTRIES, removeDash } from '@pelicargo/utils';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { OnboardingLayout } from '../../components/OnboardingLayout';
import { Page } from '../../components/Page';
import { SimpleCheckbox } from '../../components/SimpleCheckbox';
import { SimpleForm } from '../../components/SimpleForm';
import { SimpleInput } from '../../components/SimpleInput';
import { SimpleReactSelect } from '../../components/SimpleReactSelect';
import { trpc } from '../../config/trpc';
import { useAuth } from '../../hooks/auth/useAuth';
import { useOnboarding } from '../../hooks/onboarding/useOnboarding';
import { useScreenView } from '../../hooks/useScreenView';
import { formatIataCode } from '../../utils/inputFormatters';
import { MixpanelEvent, trackEvent } from '../../utils/mx';

const schema = z
  .object({
    company_name: z.string({ required_error: 'Company name is required' }).min(2, {
      message: 'Company name is required',
    }),
    country: z.string({ required_error: 'Country is required' }),
    iata_number: z.string().nullish(),
    no_iata_number: z.boolean().nullish(),
    has_iac: z.boolean().nullish(),
    is_open_to_domain_whitelist: z.boolean().nullish(),
  })
  .superRefine(({ iata_number, no_iata_number }, ctx) => {
    if (!no_iata_number && !iata_number) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `IATA code is required.`,
        path: ['iata_number'],
      });
    }

    if (!no_iata_number && removeDash(iata_number).length < 11) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `IATA code must be 11 digits.`,
        path: ['iata_number'],
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export const RegisterCompany = () => {
  const { setOnboardingStep } = useOnboarding();
  useScreenView(MixpanelEvent.CompanyView);

  const { data } = trpc.getAccountInvites.useQuery();
  const tenant = getTenantByHost();

  const methods = useForm<Partial<FormValues>>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    // TODO: change defaults for hasTypes
    defaultValues: {
      company_name: '',
      country: 'US',
      iata_number: '',
      no_iata_number: !tenant.accountTypes.IATA,
      has_iac: tenant.accountTypes.IAC,
      is_open_to_domain_whitelist: true,
    },
  });

  const { user } = useAuth();
  const workspaceDomain = useMemo(() => {
    // Get all unique domains
    const domain = user?.email?.split('@')?.[1] || '';
    return `@${domain}`;
  }, [user?.email]);

  const { mutateAsync: createAccount, isLoading: isSubmitting } = trpc.createAccount.useMutation();

  const no_iata_number = methods.watch('no_iata_number');

  const handleBack = useCallback(async () => {
    trackEvent(MixpanelEvent.CompanyBackToInvites);
    await setOnboardingStep(OnboardingStep.COMPANY_SELECT);
  }, [setOnboardingStep]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      trackEvent(MixpanelEvent.CompanySubmit);
      await createAccount({
        company_name: values.company_name,
        country: values.country,
        iata_number: values.iata_number,
        has_iac: values.has_iac,
        is_open_to_domain_whitelist: values.is_open_to_domain_whitelist,
      });
    },
    [createAccount],
  );

  return (
    <Page title="Company" showHeader={false} showFooter={false}>
      <OnboardingLayout heading="Register company" step={1}>
        <SimpleForm {...methods} onSubmit={handleSubmit}>
          <Stack spacing="3">
            <SimpleInput name="company_name" label="Company name" placeholder="Company name" variant="outline" />
            <SimpleReactSelect
              name="country"
              label="Country/Region"
              placeholder="Country"
              variant="outline"
              options={COUNTRIES.map((country) => ({
                label: country?.name,
                value: country?.code,
              }))}
            />
            <Show if={!no_iata_number}>
              <SimpleInput
                name="iata_number"
                label="IATA code"
                placeholder="IATA code"
                variant="outline"
                formatter={formatIataCode}
                maxLength={13}
              />
            </Show>
            {/* Don't even present the option if IATA isn't supported. If that's the case, you're defaulting to no_iata_number */}
            <Show if={tenant.accountTypes.IATA}>
              <SimpleCheckbox name="no_iata_number" label="Sign up without an IATA code" variant="unstyled" />
            </Show>
            <Show if={no_iata_number && tenant.accountTypes.IAC}>
              <SimpleCheckbox name="has_iac" label="Company has an IAC" variant="unstyled" />
            </Show>

            <SimpleCheckbox name="is_open_to_domain_whitelist" colorScheme="primary" variant="unstyled">
              Let anyone with a <b>{workspaceDomain}</b> email join this workspace
            </SimpleCheckbox>

            <Button colorScheme="primary" size="lg" alignSelf="flex-end" type="submit" isLoading={isSubmitting}>
              Continue
            </Button>
          </Stack>
        </SimpleForm>
        <Show if={data?.length > 0}>
          <Stack align="center">
            <Text textStyle="body">Trying to join your company on {getTenantByHost().name}?</Text>
            <Button variant="link" colorScheme="primary" onClick={handleBack}>
              Accept an invite
            </Button>
          </Stack>
        </Show>
      </OnboardingLayout>
    </Page>
  );
};
